import { OrdinaryObjectNumber } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { TableCellType } from "./table-cell-type.enum";
import { TableColumn } from "./table-column.model";
import { TableData } from "./table-data.model";

export class TableSizer {

    static instance = new TableSizer();

    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;

    private dateTimeWidth: number = null;
    private dateWidth: number = null;
    private durationWidth: number = null;
    private byteSizeWidth: number = null;
    private yesNoWidth: number = null;
    private buttonWidth = 20;
    private padding = 20;
    private numberWidth: OrdinaryObjectNumber<number> = {};
    private textWidth: OrdinaryObjectNumber<number> = {};

    constructor() {
        this.canvas = document.createElement("canvas");
        this.context = this.canvas.getContext("2d");
        this.context.font = "12px sans-serif";
    }

    calculate(table: TableData, col: TableColumn, byContent: boolean, useHeaderWidthAsMin = true): { width: number; flex: number } {
        if (col.flex != null) {
            if (byContent) {
                let max = 0;
                for (let row of table.rows) {
                    max = Math.max(max, this.measure(Utils.toString(col.formatValue(row.values[col.id]))) + this.padding);
                }
                max = Math.max(max, this.measure(col.caption) * 1.2 + this.padding);
                return { width: null, flex: max };
            }
            else {
                return { width: null, flex: col.flex };
            }
        }
        
        let headerWidth = useHeaderWidthAsMin || col.autoWidth == null ? this.measure(col.caption) * 1.2 + this.padding : 0;
        if (col.autoWidth == null) {
            return { width: headerWidth, flex: null };
        }

        switch (col.cellType) {
            case TableCellType.dateTime:
                if (this.dateTimeWidth == null) {
                    this.dateTimeWidth = this.measure("00:00:00 00:00:00") + this.padding;
                }
                return { width: Math.max(headerWidth, this.dateTimeWidth), flex: null };
            case TableCellType.date:
                if (this.dateWidth == null) {
                    this.dateWidth = this.measure("00:00:00") + this.padding;
                }
                return { width: Math.max(headerWidth, this.dateWidth), flex: null };
            case TableCellType.duration:
            case TableCellType.durationNanos:
                if (this.durationWidth == null) {
                    this.durationWidth = this.measure("100s 100ms") + this.padding;
                }
                return { width: Math.max(headerWidth, this.durationWidth), flex: null };
            case TableCellType.byteSize:
                if (this.byteSizeWidth == null) {
                    this.byteSizeWidth = this.measure("1000 Bytes") + this.padding;
                }
                return { width: Math.max(headerWidth, this.byteSizeWidth), flex: null };
            case TableCellType.yesNo:
                if (this.yesNoWidth == null) {
                    this.yesNoWidth = 60;
                }
                return { width: Math.max(headerWidth, this.yesNoWidth), flex: null };
            case TableCellType.button:
                return { width: Math.max(headerWidth, this.buttonWidth * col.autoWidth + this.padding), flex: null };
            case TableCellType.int:
                let w = this.numberWidth[col.autoWidth];
                if (w == null) {
                    w = this.measure("0".repeat(Math.min(100, col.autoWidth))) + this.padding;
                    this.numberWidth[col.autoWidth] = w;
                }
                return { width: Math.max(headerWidth, w), flex: null };
            case TableCellType.decimal:
                let w3 = this.numberWidth[col.autoWidth];
                if (w3 == null) {
                    w3 = this.measure("0".repeat(Math.min(100, col.autoWidth))) + this.padding;
                    this.numberWidth[col.autoWidth] = w3;
                }
                return { width: Math.max(headerWidth, w3), flex: null };
            case TableCellType.text:
                let w2 = this.textWidth[col.autoWidth];
                if (w2 == null) {
                    w2 = this.measure("s".repeat(Math.min(1000, col.autoWidth))) + this.padding;
                    this.textWidth[col.autoWidth] = w2;
                }
                return { width: Math.max(headerWidth, w2), flex: null };
            default:
                throw new Error("Konnte Breite für '" + col.autoWidth + "' nicht berechnen. Nicht unterstützter Datentyp '" + col.cellType + "'");
        }
    }

    private measure(text: string): number {
        return this.context.measureText(text).width;
    }
}
